import { SelectionModel } from '@angular/cdk/collections';
import { BehaviorSubject } from 'rxjs';

import { Component, CUSTOM_ELEMENTS_SCHEMA, Inject, Input } from '@angular/core';
import { ClientsWithSameNameItem } from '@skylab-shared/business';
import { AppStates } from '@skylab-shared/scenes';

import { L10n, L10nBase } from '@skylab-angular-shared/core';
import { TranslatePipe } from '@skylab-angular-shared/pipes';
import { ButtonComponent, DialogContentComponentBase, DialogContentComponentIns, DialogContentViewModel, DialogHeaderComponentIns, DialogInsService, DialogState, GridInsComponent, GridTemplateDirective, ServiceGridColumn, ServiceGridOptions } from '@skylab-angular-shared/ui';

type SelectDuplicatedClientGridColumnIdentity = 'name' | 'address' | 'select-button';

interface SelectDuplicatedClientDialogViewModel extends DialogContentViewModel {
    isFormReady: boolean;
    onClientSelect: (item: ClientsWithSameNameItem) => void;
    gridOptions: ServiceGridOptions<ClientsWithSameNameItem, SelectDuplicatedClientGridColumnIdentity, unknown>;
    gridColumns$: BehaviorSubject<ServiceGridColumn<SelectDuplicatedClientGridColumnIdentity, unknown, ClientsWithSameNameItem, unknown, any>[]>;
    gridDataSource$: BehaviorSubject<ClientsWithSameNameItem[]>;
}

export type SelectDuplicatedClientDialogResult = {
    clientId: number | null;
}

@Component({
    selector: 'skl-select-duplicated-client-dialog',
    templateUrl: './select-duplicated-client-dialog.component.html',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DialogContentComponentIns, DialogHeaderComponentIns, GridInsComponent, GridTemplateDirective, ButtonComponent, TranslatePipe]
})
export class SelectDuplicatedClientDialogComponent extends DialogContentComponentBase<SelectDuplicatedClientDialogViewModel, SelectDuplicatedClientDialogResult> {
    @Input() public collection!: ClientsWithSameNameItem[];

    public constructor(
        dialogService: DialogInsService,
        @Inject(L10n) private readonly l10n: L10nBase,
    ) {
        super(dialogService);
    }

    protected override getInitialState(): SelectDuplicatedClientDialogViewModel {
        return {
            ...super.getInitialState(),
            gridDataSource$: new BehaviorSubject(this.collection),
            gridColumns$: new BehaviorSubject(this.gridOptions),
            gridOptions: {
                selection: new SelectionModel(),
                stickyHeader: false,
            },
            isFormReady: false,
            onClientSelect: this.onClientSelect.bind(this)
        };
    }

    protected override isReady(): void {
        this.viewModel.isFormReady = true;
    }

    public save(): void {
        this.dialogService.close<SelectDuplicatedClientDialogResult>({
            state: DialogState.Confirm,
            data: {
                clientId: null,
            }
        });
    }

    public onClientSelect(item: ClientsWithSameNameItem): void {
        this.dialogService.close<SelectDuplicatedClientDialogResult>({
            state: DialogState.Confirm,
            data: {
                clientId: item.id,
            }
        });
    }

    private get gridOptions(): ServiceGridColumn<SelectDuplicatedClientGridColumnIdentity, unknown, ClientsWithSameNameItem, unknown, any>[] {
        return [
            {
                identityName: 'name',
                columnDef: 'name',
                minWidth: '80px',
                cell: entity => entity.name,
                cellTooltip: entity => entity.name,
                header: this.l10n._T('scenes.shared.dialog.select-duplicated-client.form.grid.name'),
                template: 'sklHrefLinkTemplate',
                getRouteObject: (entity: ClientsWithSameNameItem) => ({ url: AppStates.editClient.url, params: { id: entity.id } }),
                target: '_blank',
                fillingColumn: true,
                isResizable: false,
                isMovable: false
            },
            {
                identityName: 'address',
                columnDef: 'address',
                cell: entity => [entity.city, entity.address].filter(Boolean).map(x => x.trim()).join(', '),
                cellTooltip: entity => [entity.city, entity.address].filter(Boolean).map(x => x.trim()).join(', '),
                header: this.l10n._T('scenes.shared.dialog.select-duplicated-client.form.grid.address'),
                minWidth: '240px',
                isResizable: false,
                isMovable: false
            },
            {
                identityName: 'select-button',
                template: 'selectDuplicatedClientSelectTemplate',
                columnDef: 'select-button',
                width: '70px',
                isResizable: false,
                isMovable: false
            }
        ];
    }
}
